// src/components/OrderModal.js
import React, { useEffect, useState } from 'react';
import '../pages/AdminOrders.css';
import { adminFetch } from '../api/admin';
import { useToast } from './ToastProvider';
import { Send, Clock, Calendar } from 'lucide-react';

function formatPrice(p) {
  if (p == null || p === undefined) return '—';
  if (typeof p === 'number') {
    return `₹${p.toFixed(2)}`;
  }
  return String(p);
}

// ⏱ Helper: Calculate duration between two dates
function calculateDuration(startDate, endDate) {
  if (!startDate || !endDate) return null;

  const start = new Date(startDate);
  const end = new Date(endDate);

  if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;

  const diffMs = end - start;
  if (diffMs < 0) return null; // Should not happen in chronological timeline

  const diffMins = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHrs / 24);

  if (diffDays > 0) {
    const remainingHrs = diffHrs % 24;
    return `${diffDays}d ${remainingHrs}h`;
  }

  if (diffHrs > 0) {
    const remainingMins = diffMins % 60;
    return `${diffHrs}h ${remainingMins}m`;
  }

  return `${diffMins}m`;
}

export default function OrderModal({ order, onClose }) {
  const { showToast } = useToast();
  const [resending, setResending] = useState(false);
  const [fullOrder, setFullOrder] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  // Fetch full details (including history) when modal opens
  useEffect(() => {
    if (order?.id) {
      setLoadingHistory(true);
      adminFetch(`get-order.php?order_id=${order.id}`)
        .then(data => {
          if (data.ok) {
            setFullOrder(data.order);
          }
        })
        .catch(err => console.error("Failed to fetch order details", err))
        .finally(() => setLoadingHistory(false));
    }
  }, [order?.id]);

  if (!order) return null;

  // Use fullOrder if available (for history), otherwise fallback to props
  const displayOrder = fullOrder || order;
  const items = displayOrder?.items ?? [];

  // Ensure status history is sorted chronologically
  const statusHistory = [...(displayOrder?.status_history ?? [])].sort((a, b) =>
    new Date(a.changed_at) - new Date(b.changed_at)
  );

  // Construct full timeline events
  const timelineEvents = [];

  // 1. Order Placed (Always first)
  if (order.created_at) {
    timelineEvents.push({
      status: 'Order Placed',
      date: order.created_at,
      color: '#d14c87' // Brand Pink
    });
  }

  // 2. Status Updates
  statusHistory.forEach(entry => {
    const isDelivery = entry.status === 'delivered';
    timelineEvents.push({
      status: entry.status,
      date: entry.changed_at,
      color: isDelivery ? '#4caf50' : '#d14c87'
    });
  });

  // Handle Resend Email
  const handleResendEmail = async (type) => {
    if (resending) return;
    setResending(true);

    try {
      const res = await adminFetch('resend-email.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: order.id,
          type: type
        })
      });

      const data = await res.json();

      if (data.ok) {
        showToast(data.message || 'Email resent successfully', 'success');
      } else {
        throw new Error(data.error || 'Failed to resend');
      }
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setResending(false);
    }
  };

  return (
    <>
      <div className="admin-modal-backdrop" onClick={onClose} />
      <div className="admin-modal" role="dialog" aria-modal="true" aria-label={`Order ${order.id} details`}>
        <div className="admin-modal-card">
          <button className="admin-modal-close" onClick={onClose} aria-label="Close order details">
            ×
          </button>

          <h3 className="admin-modal-title">Order #{order.id}</h3>

          <div className="admin-modal-grid">
            <div>
              <p className="admin-label">Customer</p>
              <p className="admin-value">{order.customer_name || '—'}</p>
            </div>
            <div>
              <p className="admin-label">Email</p>
              <p className="admin-value">{order.email || '—'}</p>
            </div>
            <div>
              <p className="admin-label">Phone</p>
              <p className="admin-value">{order.phone || '—'}</p>
            </div>
            <div>
              <p className="admin-label">Placed</p>
              <p className="admin-value">{new Date(order.created_at).toLocaleString() || '—'}</p>
            </div>
          </div>

          {/* 🔄 RESEND EMAIL ACTIONS */}
          <div className="admin-block" style={{ marginTop: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              className="btn-pill secondary"
              onClick={() => handleResendEmail('order_confirmation')}
              disabled={resending}
              style={{ fontSize: '0.85rem' }}
            >
              <Send size={14} />
              {resending ? 'Sending...' : 'Resend Confirmation'}
            </button>

            {order.status !== 'pending' && order.status !== 'processing' && (
              <button
                className="btn-pill secondary"
                onClick={() => handleResendEmail('status_update')}
                disabled={resending}
                style={{ fontSize: '0.85rem' }}
              >
                <Send size={14} />
                {resending ? 'Sending...' : 'Resend Status Email'}
              </button>
            )}
          </div>

          <div className="admin-block">
            <p className="admin-label">Address</p>
            <p className="admin-value">{order.address || '—'}</p>
          </div>

          <div className="admin-block">
            <p className="admin-label">Items</p>
            {items.length > 0 ? (
              <div className="admin-items-list">
                {items.map((item, idx) => {
                  const variantLabel = item?.variant_label || item?.variant;
                  const itemName = variantLabel ? `${item.name} (${variantLabel})` : (item?.name || '—');
                  const qty = item?.qty ?? 1;
                  const price = item?.price ?? 0;
                  const itemId = item?.id ?? 0;
                  return (
                    <div key={`${itemId}-${idx}`} className="admin-item-row">
                      <div>
                        <p className="admin-item-name">{itemName}</p>
                        {itemId > 0 && <p className="admin-item-meta">ID: {itemId}</p>}
                      </div>
                      <div className="admin-item-meta">Qty: {qty}</div>
                      <div className="admin-item-meta">Price: {formatPrice(price)}</div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="admin-value">No items</p>
            )}
          </div>

          {/* 📅 ORDER TIMELINE */}
          <div className="admin-block">
            <p className="admin-label">Order Timeline</p>
            <div className="admin-status-timeline" style={{ position: 'relative', paddingLeft: '20px', borderLeft: '2px solid #f0f0f0', marginLeft: '10px' }}>
              {timelineEvents.map((event, idx) => {
                const prevEvent = idx > 0 ? timelineEvents[idx - 1] : null;
                const duration = prevEvent ? calculateDuration(prevEvent.date, event.date) : null;

                return (
                  <div key={idx} style={{ marginBottom: '16px', position: 'relative' }}>
                    {/* Dot */}
                    <div style={{
                      position: 'absolute',
                      left: '-26px',
                      top: '4px',
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      background: event.color,
                      border: '2px solid #fff',
                      boxShadow: '0 0 0 1px rgba(0,0,0,0.1)'
                    }} />

                    {/* Duration Label */}
                    {duration && (
                      <div style={{
                        fontSize: '0.75rem',
                        color: '#9E9E9E',
                        marginBottom: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <Clock size={10} />
                        {duration} later
                      </div>
                    )}

                    {/* Card */}
                    <div className="admin-timeline-item" style={{
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: '2px',
                      borderLeft: `3px solid ${event.color}`
                    }}>
                      <div className="admin-timeline-status" style={{ fontSize: '0.95rem' }}>
                        {event.status}
                      </div>
                      <div className="admin-timeline-date" style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Calendar size={12} />
                        {new Date(event.date).toLocaleString()}
                      </div>
                    </div>
                  </div>
                );
              })}

              {timelineEvents.length === 0 && (
                <p className="admin-value admin-muted">No timeline data available</p>
              )}
            </div>
          </div>

          <div className="admin-summary">
            <div>
              <p className="admin-label">Status</p>
              <p className={`admin-chip ${order.status === 'delivered' ? 'admin-chip-delivered' : ''}`}>
                {order.status || '—'}
              </p>
            </div>

            {/* Shipping Breakdown */}
            <div style={{ textAlign: 'right' }}>
              <p className="admin-label">Subtotal</p>
              <p className="admin-value">{formatPrice(order.subtotal ?? (order.total - (order.shipping || 0)))}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p className="admin-label">Shipping</p>
              <p className="admin-value" style={{ color: order.shipping == 0 ? '#4CAF50' : 'inherit' }}>
                {order.shipping == 0 ? 'FREE' : formatPrice(order.shipping)}
              </p>
            </div>

            <div>
              <p className="admin-label">Total</p>
              <p className="admin-total">{formatPrice(order.total)}</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

